import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Plus, Search, Filter, Download, RefreshCw, X, 
  ChevronDown, Calendar, DollarSign, Trash2, 
  FileText, CheckCircle2, Loader2, Check,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Tag, Clock, Info, Users, LayoutGrid, Settings2, ImageIcon, Link2,
  MoreHorizontal, LayoutList, ChevronRight, FolderKanban
} from 'lucide-react';
import { Project, ProjectStatus, Client, User, Task, TaskStatus, Priority } from './types';
import { Card, Badge, InfoItem } from './SharedComponents';

export const ProjectsPage = ({ clients, users, currentUser, projects, tasks, onUpdateProjects, onAddTask, onUpdateTask, onDeleteTask, onNavigate }: { 
  clients: Client[], 
  users: User[], 
  currentUser: User,
  projects: Project[],
  tasks: Task[],
  onUpdateProjects: (projects: Project[]) => void,
  onAddTask: (task: Partial<Task>) => void,
  onUpdateTask: (task: Partial<Task>) => void,
  onDeleteTask: (id: string) => void,
  onNavigate?: (tab: string) => void 
}) => {
  const [search, setSearch] = useState('');
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | null>(null);
  const [pageSize, setPageSize] = useState(25);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados dos Filtros Avançados
  const [dateFilter, setDateFilter] = useState('');
  const [memberFilter, setMemberFilter] = useState('Todos os Membros');

  const stats = useMemo(() => ({
    notStarted: projects.filter(p => p.status === ProjectStatus.NAO_INICIADO).length,
    inProgress: projects.filter(p => p.status === ProjectStatus.EM_PROGRESSO).length,
    waiting: projects.filter(p => p.status === ProjectStatus.AGUARDANDO).length,
    cancelled: projects.filter(p => p.status === ProjectStatus.CANCELADO).length,
    completed: projects.filter(p => p.status === ProjectStatus.CONCLUIDO).length,
  }), [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      const client = clients.find(c => c.id === p.clientId);
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                          (client?.empresa || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !statusFilter || p.status === statusFilter;
      const matchesDate = !dateFilter || p.startDate === dateFilter;
      const matchesMember = memberFilter === 'Todos os Membros' || p.members.some(mid => users.find(u => u.id === mid)?.name === memberFilter);
      
      return matchesSearch && matchesStatus && matchesDate && matchesMember;
    });
  }, [projects, search, statusFilter, clients, dateFilter, memberFilter, users]);

  const toggleStatusFilter = (status: ProjectStatus) => {
    if (statusFilter === status) {
      setStatusFilter(null);
    } else {
      setStatusFilter(status);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter(null);
    setDateFilter('');
    setMemberFilter('Todos os Membros');
  };

  const handleSaveProject = async (newProject: any) => {
    setIsSaving(true);
    try {
      const saved = await (import('./supabase').then(m => m.db.saveProject(newProject, currentUser.companyId || ''))) as any;
      if (editingProject) {
        onUpdateProjects(projects.map(p => p.id === saved.id ? saved : p) as any);
        setEditingProject(null);
      } else {
        onUpdateProjects([saved, ...projects] as any);
      }
      setIsNewModalOpen(false);
    } catch (e) {
      console.error("Erro ao salvar projeto:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',');
        
        const newProjects: Project[] = lines.slice(1).filter(line => line.trim()).map(line => {
          const values = line.split(',');
          const p: any = {
            id: `p-csv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            company_id: currentUser.companyId,
            status: ProjectStatus.NAO_INICIADO,
            progress: 0,
            tags: [],
            members: [currentUser.id],
            visibleTabs: ['Tarefas', 'Quadros de Tempo', 'Marcos', 'Arquivos', 'Discussões', 'Visualização Gantt', 'Solicitações', 'Contratos', 'Propostas', 'Orçamento'],
            permissions: {
              viewTasks: true,
              createTasks: true,
              editTasks: true,
              commentTasks: true,
              viewTaskComments: true,
              viewTaskAttachments: true,
              viewTaskChecklist: true,
              uploadAttachments: true,
              viewTaskTotalTime: true,
              viewFinanceOverview: true,
              updateFiles: true,
              openDiscussions: true,
              viewMilestones: true,
              viewGantt: true,
              viewTimesheets: true,
              viewActivityLog: true,
              viewTeamMembers: true,
              hideTasksFromAdmin: false
            }
          };
          
          headers.forEach((header, i) => {
            const h = header.trim().toLowerCase();
            const v = values[i]?.trim();
            if (h === 'nome' || h === 'name') p.name = v;
            if (h === 'cliente' || h === 'client') {
              const client = clients.find(c => c.empresa.toLowerCase() === v.toLowerCase());
              if (client) p.clientId = client.id;
            }
            if (h === 'inicio' || h === 'start') p.startDate = v;
            if (h === 'prazo' || h === 'deadline') p.deadline = v;
            if (h === 'descricao' || h === 'description') p.description = v;
          });
          
          return p as Project;
        });

        const validProjects = newProjects.filter(p => p.name && p.clientId);
        
        if (validProjects.length > 0) {
          // Em um app real, salvaríamos no Supabase aqui
          onUpdateProjects([...validProjects, ...projects]);
          alert(`${validProjects.length} projetos importados com sucesso!`);
        } else {
          alert("Nenhum projeto válido encontrado no CSV. Certifique-se de que as colunas 'Nome' e 'Cliente' estão presentes e preenchidas.");
        }
      } catch (error) {
        console.error("Erro ao processar CSV:", error);
        alert("Erro ao processar o arquivo CSV.");
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setIsNewModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 px-6 md:px-12 lg:px-16 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => { setEditingProject(null); setIsNewModalOpen(true); }}
            className="bg-[#0f172a] text-white px-5 py-2.5 rounded-lg font-bold text-xs flex items-center gap-2 hover:bg-black transition-all shadow-md active:scale-95"
          >
            <Plus size={18} strokeWidth={3} /> Novo Projeto
          </button>
          
          <div className="flex p-1 bg-white border border-gray-200 rounded-lg shadow-sm">
            <button 
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-primary/10 text-primary' : 'text-slate-400 hover:bg-slate-50'}`}
              title="Visualização em Lista"
            >
              <LayoutList size={18} />
            </button>
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-slate-400 hover:bg-slate-50'}`}
              title="Visualização em Grid"
            >
              <LayoutGrid size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <StatusBadge label="Não Iniciado" value={stats.notStarted} color="text-slate-500" onClick={() => toggleStatusFilter(ProjectStatus.NAO_INICIADO)} active={statusFilter === ProjectStatus.NAO_INICIADO} />
          <StatusBadge label="Em Progresso" value={stats.inProgress} color="text-blue-500" onClick={() => toggleStatusFilter(ProjectStatus.EM_PROGRESSO)} active={statusFilter === ProjectStatus.EM_PROGRESSO} />
          <StatusBadge label="Aguardando" value={stats.waiting} color="text-orange-500" onClick={() => toggleStatusFilter(ProjectStatus.AGUARDANDO)} active={statusFilter === ProjectStatus.AGUARDANDO} />
          <StatusBadge label="Cancelado" value={stats.cancelled} color="text-red-500" onClick={() => toggleStatusFilter(ProjectStatus.CANCELADO)} active={statusFilter === ProjectStatus.CANCELADO} />
          <StatusBadge label="Concluído" value={stats.completed} color="text-green-600" onClick={() => toggleStatusFilter(ProjectStatus.CONCLUIDO)} active={statusFilter === ProjectStatus.CONCLUIDO} />
        </div>
        
        <div className="relative">
          <button 
            onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
            className={`bg-white border border-gray-200 px-4 py-2.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all shadow-sm ${isFilterDropdownOpen ? 'border-primary text-primary' : 'text-slate-600 hover:bg-gray-50'}`}
          >
            <Filter size={16} /> Filtros {isFilterDropdownOpen && <ChevronDown size={14} className="rotate-180" />}
          </button>
          
          {isFilterDropdownOpen && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-6 z-[200] animate-in zoom-in-95 duration-200">
               <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4">Filtragem Avançada</h4>
               <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Data de Início</label>
                    <input 
                      type="date" 
                      className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-bold outline-none" 
                      value={dateFilter}
                      onChange={e => setDateFilter(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Membros</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-bold outline-none"
                      value={memberFilter}
                      onChange={e => setMemberFilter(e.target.value)}
                    >
                       <option value="Todos os Membros">Todos os Membros</option>
                       {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  </div>
                  <button 
                    onClick={() => setIsFilterDropdownOpen(false)}
                    className="w-full py-2.5 bg-primary text-white rounded-lg font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 hover:brightness-110 active:scale-95"
                  >
                    Aplicar Filtros
                  </button>
                  <button 
                    onClick={() => { resetFilters(); setIsFilterDropdownOpen(false); }}
                    className="w-full py-1.5 text-[9px] font-black uppercase text-slate-400 hover:text-red-500"
                  >
                    Limpar Tudo
                  </button>
               </div>
            </div>
          )}
        </div>
      </div>

      <Card className="bg-white border border-gray-100 shadow-sm rounded-xl overflow-hidden mt-4">
        <div className="p-4 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <select 
              className="bg-white border border-gray-200 p-2 rounded-lg text-xs font-bold text-slate-500 outline-none" 
              value={pageSize} 
              onChange={e => setPageSize(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
              className="bg-white border border-gray-200 px-4 py-2 rounded-lg text-xs font-bold text-slate-600 hover:bg-gray-50 flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              {isImporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Importar CSV
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".csv" 
              onChange={handleImportCSV} 
            />
            <button 
              onClick={resetFilters}
              className="p-2.5 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-primary transition-all shadow-sm"
              title="Resetar Filtros"
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="relative group w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input 
              placeholder="Procurar projeto ou cliente..." 
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-transparent rounded-lg outline-none text-xs font-medium focus:bg-white focus:ring-2 focus:ring-primary/20 transition-all" 
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-[10px] uppercase font-black tracking-widest border-b border-gray-100">
                  <th className="px-6 py-4 w-10">#</th>
                  <th className="px-4 py-4">Nome do Projeto</th>
                  <th className="px-4 py-4">Cliente</th>
                  <th className="px-4 py-4">Tags</th>
                  <th className="px-4 py-4">Data de Início</th>
                  <th className="px-4 py-4">Prazo <ChevronDown size={12} className="inline ml-1" /></th>
                  <th className="px-4 py-4">Membros do Projeto</th>
                  <th className="px-4 py-4 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredProjects.length > 0 ? filteredProjects.map((p, idx) => (
                  <tr 
                    key={p.id} 
                    onClick={() => setSelectedProject(p)}
                    className="hover:bg-primary/[0.03] transition-all text-[#1e293b] group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-xs font-medium text-slate-400">{idx + 1}</td>
                    <td className="px-4 py-4 text-xs font-bold">{p.name}</td>
                    <td className="px-4 py-4 text-xs font-medium uppercase text-slate-500">
                      {clients.find(c => c.id === p.clientId)?.empresa || '---'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1 flex-wrap">
                        {p.tags.map(t => <span key={t} className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">{t}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500">{p.startDate}</td>
                    <td className="px-4 py-4 text-xs text-slate-500">{p.deadline || '---'}</td>
                    <td className="px-4 py-4">
                      <div className="flex -space-x-2">
                        {p.members.slice(0, 3).map(mid => (
                          <div key={mid} className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[8px] font-black text-slate-600" title={users.find(u => u.id === mid)?.name}>
                            {users.find(u => u.id === mid)?.name.charAt(0)}
                          </div>
                        ))}
                        {p.members.length > 3 && (
                          <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-slate-400">
                            +{p.members.length - 3}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Badge className={`text-[10px] ${getProjectStatusStyle(p.status)}`}>{p.status}</Badge>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="px-8 py-20 text-center text-gray-300 font-bold uppercase tracking-widest italic bg-white">
                      Nenhuma entrada encontrada para os filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-8">
             {filteredProjects.map(p => (
               <Card 
                key={p.id} 
                onClick={() => setSelectedProject(p)}
                className="bg-white border-2 border-gray-50 hover:border-primary/20 hover:shadow-xl transition-all p-6 space-y-6 cursor-pointer group flex flex-col"
               >
                  <div className="flex justify-between items-start">
                     <Badge className={`text-[8px] ${getProjectStatusStyle(p.status)}`}>{p.status}</Badge>
                     <button className="text-gray-300 hover:text-primary"><MoreHorizontal size={16} /></button>
                  </div>
                  <div>
                    <h3 className="font-black text-secondary uppercase tracking-tight line-clamp-1 group-hover:text-primary transition-colors">{p.name}</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">{clients.find(c => c.id === p.clientId)?.empresa || '---'}</p>
                  </div>
                  <div className="space-y-2">
                     <div className="flex justify-between items-center text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        <span>Progresso</span>
                        <span className="text-secondary">{p.progress}%</span>
                     </div>
                     <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-primary h-full transition-all duration-700" style={{ width: `${p.progress}%` }}></div>
                     </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                     <div className="flex -space-x-2">
                        {p.members.slice(0, 3).map(mid => (
                          <div key={mid} className="w-7 h-7 rounded-lg bg-gray-50 border-2 border-white flex items-center justify-center text-[10px] font-black text-slate-500 shadow-sm">
                             {users.find(u => u.id === mid)?.name.charAt(0)}
                          </div>
                        ))}
                     </div>
                     <div className="text-right">
                        <p className="text-[8px] font-black text-gray-300 uppercase">Início</p>
                        <p className="text-[10px] font-bold text-slate-500">{p.startDate}</p>
                     </div>
                  </div>
               </Card>
             ))}
          </div>
        )}
      </Card>

      {/* MODAL NOVO PROJETO / EDITAR */}
      {isNewModalOpen && (
        <NewProjectModal 
          key={editingProject?.id || 'new-project'}
          clients={clients} 
          users={users}
          project={editingProject}
          currentUser={currentUser}
          onClose={() => { setIsNewModalOpen(false); setEditingProject(null); }} 
          onSave={handleSaveProject} 
          isSaving={isSaving}
        />
      )}

      {/* PAINEL DE DETALHES DO PROJETO */}
      {selectedProject && (
        <ProjectDetailsPanel 
          project={selectedProject} 
          client={clients.find(c => c.id === selectedProject.clientId)}
          users={users}
          tasks={tasks.filter(t => t.projectId === selectedProject.id)}
          onClose={() => setSelectedProject(null)} 
          onNavigate={onNavigate}
          onEdit={() => handleEditProject(selectedProject)}
          onAddTask={onAddTask}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
        />
      )}
    </div>
  );
};

const ProjectDetailsPanel = ({ project, client, users, tasks, onClose, onNavigate, onEdit, onAddTask, onUpdateTask, onDeleteTask }: any) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'files' | 'discussions'>('overview');

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300] flex justify-end">
      <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-gray-100">
        <div className="p-10 bg-[#0f172a] text-white flex flex-col gap-6 shrink-0 relative overflow-hidden">
           <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12 scale-150"><FolderKanban size={200} /></div>
           <div className="flex justify-between items-start relative z-10">
              <Badge className={getProjectStatusStyle(project.status)}>{project.status}</Badge>
              <button onClick={onClose} className="p-3 bg-white/5 hover:bg-red-500/20 rounded-2xl transition-all active:scale-90"><X size={28} /></button>
           </div>
           <div className="relative z-10">
              <h2 className="text-3xl font-black uppercase tracking-tight italic leading-none">{project.name}</h2>
              <p className="text-primary font-black uppercase text-[10px] tracking-[0.3em] mt-3">{client?.empresa || '---'}</p>
           </div>
           
           <div className="flex gap-6 mt-4 relative z-10">
              <button 
                onClick={() => setActiveTab('overview')}
                className={`text-[10px] font-black uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === 'overview' ? 'border-primary text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                Visão Geral
              </button>
              <button 
                onClick={() => setActiveTab('tasks')}
                className={`text-[10px] font-black uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === 'tasks' ? 'border-primary text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                Tarefas
              </button>
              <button 
                onClick={() => setActiveTab('files')}
                className={`text-[10px] font-black uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === 'files' ? 'border-primary text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                Arquivos
              </button>
              <button 
                onClick={() => setActiveTab('discussions')}
                className={`text-[10px] font-black uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === 'discussions' ? 'border-primary text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                Discussões
              </button>
           </div>

           <div className="mt-2 relative z-10 flex gap-4">
              <div className="flex-1 space-y-2">
                 <div className="flex justify-between text-[10px] font-black uppercase tracking-widest opacity-40">
                    <span>Progresso do Projeto</span>
                    <span>{project.progress}%</span>
                 </div>
                 <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-primary shadow-[0_0_15px_rgba(0,168,224,1)] transition-all duration-1000" style={{ width: `${project.progress}%` }}></div>
                 </div>
              </div>
           </div>
        </div>

        <div className="flex-1 overflow-y-auto p-10 space-y-10 scrollbar-hide">
           {activeTab === 'overview' ? (
             <>
               <section className="space-y-6">
                  <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 flex items-center gap-2 italic">
                     <Info size={16} /> Visão Estratégica
                  </h3>
                  <div className="grid grid-cols-2 gap-8">
                     <InfoItem label="Data de Início" value={project.startDate} icon={Calendar} />
                     <InfoItem label="Prazo Final" value={project.deadline || 'Indefinido'} icon={Clock} />
                     <InfoItem label="Billing" value={project.billingType} icon={DollarSign} />
                     <InfoItem label="Horas Est." value={project.estimatedHours || '---'} icon={Clock} />
                  </div>
               </section>

               <section className="space-y-6">
                  <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 flex items-center gap-2 italic">
                     <Users size={16} /> Squad do Projeto
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                     {project.members.map((mid: string) => {
                       const member = users.find((u: any) => u.id === mid);
                       return (
                         <div key={mid} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 group hover:bg-white hover:border-primary/20 transition-all">
                            <div className="flex items-center gap-4">
                               <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center font-black text-slate-500 shadow-sm">
                                  {member?.name?.charAt(0) || 'U'}
                               </div>
                               <div>
                                  <p className="text-xs font-black text-secondary uppercase">{member?.name || 'Membro'}</p>
                                  <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">{member?.role || 'Operacional'}</p>
                               </div>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-primary transition-all" />
                         </div>
                       );
                     })}
                  </div>
               </section>

               {project.description && (
                  <section className="space-y-4">
                     <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-2 italic">
                        <FileText size={16} /> Descrição Técnica
                     </h3>
                     <div className="bg-gray-50/50 p-6 rounded-[32px] border border-gray-100 italic text-sm text-slate-600 prose prose-slate max-w-none shadow-inner" dangerouslySetInnerHTML={{ __html: project.description }} />
                  </section>
               )}

               <section className="space-y-6">
                  <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 flex items-center gap-2 italic">
                     <Clock size={16} /> Atividade Recente
                  </h3>
                  <div className="space-y-4">
                     {[
                       { user: 'Magnun', action: 'atualizou o status para', target: 'Em Progresso', time: 'Há 2 horas' },
                       { user: 'Sistema', action: 'importou o projeto via', target: 'CSV', time: 'Há 5 horas' },
                       { user: 'Admin', action: 'adicionou um novo membro:', target: 'João Silva', time: 'Ontem' },
                     ].map((activity, i) => (
                       <div key={i} className="flex gap-4 items-start">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400 shrink-0">
                             {activity.user.charAt(0)}
                          </div>
                          <div>
                             <p className="text-xs text-slate-600">
                                <span className="font-bold text-secondary">{activity.user}</span> {activity.action} <span className="font-bold text-primary">{activity.target}</span>
                             </p>
                             <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">{activity.time}</p>
                          </div>
                       </div>
                     ))}
                  </div>
               </section>
             </>
           ) : activeTab === 'tasks' ? (
             <ProjectTasks 
               project={project} 
               users={users} 
               tasks={tasks} 
               onAddTask={onAddTask}
               onUpdateTask={onUpdateTask}
               onDeleteTask={onDeleteTask}
             />
           ) : activeTab === 'files' ? (
             <ProjectFiles project={project} />
           ) : (
             <ProjectDiscussions project={project} users={users} />
           )}
        </div>

        <div className="p-8 border-t border-gray-100 bg-gray-50 flex gap-3 shrink-0 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
           <button 
            onClick={() => { onClose(); if(onNavigate) onNavigate('tasks'); }}
            className="flex-1 py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all"
           >
              Gerenciar Tarefas
           </button>
           <button 
            onClick={() => { onClose(); onEdit(); }}
            className="px-6 py-4 bg-white border-2 border-gray-100 text-gray-400 rounded-2xl hover:text-primary transition-all active:scale-90"
           >
              <Settings2 size={20} />
           </button>
        </div>
      </div>
    </div>
  );
};

const ProjectTasks = ({ project, users, tasks, onAddTask, onUpdateTask, onDeleteTask }: any) => {
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  return (
    <section className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center border-b pb-3">
        <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-2 italic">
          <CheckCircle2 size={16} /> Tarefas do Projeto
        </h3>
        <button 
          onClick={() => setIsNewTaskModalOpen(true)}
          className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline"
        >
          + Nova Tarefa
        </button>
      </div>
      <div className="space-y-3">
        {tasks.length > 0 ? tasks.map((task: Task) => (
          <div key={task.id} className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all group">
            <div className="flex items-center gap-4">
              <div className={`w-1.5 h-10 rounded-full ${task.status === TaskStatus.CONCLUIDO ? 'bg-green-500' : task.status === TaskStatus.EM_PROGRESSO ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
              <div>
                <p className="text-xs font-bold text-slate-700">{task.name}</p>
                <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">Responsável: {users.find((u: any) => u.id === task.assignedTo[0])?.name || '---'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`text-[8px] ${task.status === TaskStatus.CONCLUIDO ? 'bg-green-50 text-green-600' : task.status === TaskStatus.EM_PROGRESSO ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                {task.status}
              </Badge>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditingTask(task); setIsNewTaskModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all">
                  <Settings2 size={14} />
                </button>
                <button onClick={() => onDeleteTask(task.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        )) : (
          <div className="py-10 text-center text-slate-400 text-xs font-bold uppercase tracking-widest italic">
            Nenhuma tarefa para este projeto
          </div>
        )}
      </div>

      {isNewTaskModalOpen && (
        <TaskModal 
          onClose={() => { setIsNewTaskModalOpen(false); setEditingTask(null); }}
          onSave={(data: any) => {
            if (editingTask) onUpdateTask({ ...editingTask, ...data });
            else onAddTask({ ...data, projectId: project.id });
            setIsNewTaskModalOpen(false);
            setEditingTask(null);
          }}
          task={editingTask}
          projects={[project]}
          users={users}
        />
      )}
    </section>
  );
};

const ProjectFiles = ({ project }: any) => {
  return (
    <section className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center border-b pb-3">
        <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-2 italic">
          <ImageIcon size={16} /> Arquivos do Projeto
        </h3>
        <button className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline">
          + Upload
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[
          { name: 'Contrato_Assinado.pdf', size: '2.4 MB', type: 'PDF' },
          { name: 'Briefing_Criativo.docx', size: '1.1 MB', type: 'DOCX' },
          { name: 'Logo_Final.png', size: '5.8 MB', type: 'PNG' },
          { name: 'Cronograma.xlsx', size: '850 KB', type: 'XLSX' },
        ].map((file, i) => (
          <div key={i} className="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center gap-3 group cursor-pointer">
            <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all">
              <FileText size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-700 truncate">{file.name}</p>
              <p className="text-[9px] text-slate-400 font-bold uppercase">{file.size} • {file.type}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const ProjectDiscussions = ({ project, users }: any) => {
  return (
    <section className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-center border-b pb-3">
        <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-2 italic">
          <Link2 size={16} /> Discussões
        </h3>
        <button className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline">
          + Nova Discussão
        </button>
      </div>
      <div className="space-y-4">
        {[
          { user: users[0]?.name, message: 'Olá pessoal, acabei de subir os novos arquivos do briefing.', time: '10:30' },
          { user: users[1]?.name, message: 'Excelente! Vou revisar agora mesmo.', time: '10:35' },
          { user: users[0]?.name, message: 'Qualquer dúvida me avisem.', time: '10:40' },
        ].map((chat, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400 shrink-0">
              {chat.user?.charAt(0)}
            </div>
            <div className="flex-1 bg-gray-50 p-3 rounded-2xl rounded-tl-none border border-gray-100">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[10px] font-black text-secondary uppercase">{chat.user}</span>
                <span className="text-[8px] text-gray-400 font-bold">{chat.time}</span>
              </div>
              <p className="text-xs text-slate-600">{chat.message}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="pt-4">
        <div className="relative">
          <input 
            placeholder="Digite sua mensagem..." 
            className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none text-xs font-medium focus:bg-white focus:ring-2 focus:ring-primary/20 transition-all shadow-inner"
          />
          <button className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-primary hover:bg-primary/5 rounded-xl transition-all">
            <Plus size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

const TaskModal = ({ onClose, onSave, task, projects, users }: any) => {
  const [formData, setFormData] = useState<Partial<Task>>(task || {
    name: '',
    description: '',
    projectId: projects[0]?.id || '',
    status: TaskStatus.NAO_INICIADO,
    priority: Priority.MEDIA,
    assignedTo: [],
    tags: []
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[400] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight italic">
            {task ? 'Editar Tarefa' : 'Nova Tarefa'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-50 rounded-xl transition-all"><X size={24} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="p-8 space-y-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Nome da Tarefa</label>
            <input 
              required
              className="w-full bg-slate-50 border-2 border-transparent p-4 rounded-2xl outline-none font-bold text-slate-700 focus:border-primary/20 focus:bg-white transition-all"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</label>
              <select 
                className="w-full bg-slate-50 border-2 border-transparent p-4 rounded-2xl outline-none font-bold text-slate-700 focus:border-primary/20 focus:bg-white transition-all appearance-none"
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value as TaskStatus })}
              >
                {Object.values(TaskStatus).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Prioridade</label>
              <select 
                className="w-full bg-slate-50 border-2 border-transparent p-4 rounded-2xl outline-none font-bold text-slate-700 focus:border-primary/20 focus:bg-white transition-all appearance-none"
                value={formData.priority}
                onChange={e => setFormData({ ...formData, priority: e.target.value as Priority })}
              >
                {Object.values(Priority).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Responsáveis</label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-4 bg-slate-50 border-2 border-transparent rounded-2xl focus-within:border-primary/20 focus-within:bg-white transition-all">
              {users.map((u: User) => (
                <label key={u.id} className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer hover:text-primary transition-colors">
                  <input 
                    type="checkbox" 
                    checked={formData.assignedTo?.includes(u.id)}
                    onChange={e => {
                      const current = formData.assignedTo || [];
                      if (e.target.checked) {
                        setFormData({ ...formData, assignedTo: [...current, u.id] });
                      } else {
                        setFormData({ ...formData, assignedTo: current.filter(id => id !== u.id) });
                      }
                    }}
                    className="rounded text-primary focus:ring-primary border-gray-300"
                  />
                  {u.name}
                </label>
              ))}
            </div>
          </div>

          <div className="pt-4">
            <button 
              type="submit"
              className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all"
            >
              {task ? 'Salvar Alterações' : 'Criar Tarefa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NewProjectModal = ({ clients, users, onClose, onSave, project, currentUser, isSaving }: any) => {
  const [activeTab, setActiveTab] = useState<'project' | 'settings'>('project');
  const editorRef = useRef<HTMLDivElement>(null);

  const isManager = currentUser.role === 'ADMIN' || currentUser.role === 'SUPER_ADMIN';

  const [formData, setFormData] = useState<Project>(project || {
    id: '',
    name: '',
    clientId: '',
    status: ProjectStatus.EM_PROGRESSO,
    progress: 0,
    calculateProgressByTasks: true,
    billingType: 'Nada selecionado',
    estimatedHours: '',
    startDate: new Date().toISOString().split('T')[0],
    deadline: '',
    tags: [],
    members: [currentUser.id],
    description: '',
    sendEmailOnCreate: false,
    contactNotificationType: 'Para todos os contatos com notificações de projetos ativados',
    visibleTabs: ['Tarefas', 'Quadros de Tempo', 'Marcos', 'Arquivos', 'Discussões', 'Visualização Gantt', 'Solicitações', 'Contratos', 'Propostas', 'Orçamento'],
    permissions: {
      viewTasks: true,
      createTasks: true,
      editTasks: true,
      commentTasks: true,
      viewTaskComments: true,
      viewTaskAttachments: true,
      viewTaskChecklist: true,
      uploadAttachments: true,
      viewTaskTotalTime: true,
      viewFinanceOverview: true,
      updateFiles: true,
      openDiscussions: true,
      viewMilestones: true,
      viewGantt: true,
      viewTimesheets: true,
      viewActivityLog: true,
      viewTeamMembers: true,
      hideTasksFromAdmin: false
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalProject = {
      ...formData,
      id: formData.id || Date.now().toString(),
      description: editorRef.current?.innerHTML || ''
    };
    onSave(finalProject);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 h-[90vh] flex flex-col">
        
        <div className="px-8 py-4 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
           <h2 className="text-lg font-black text-slate-800">{project ? 'Editar Projeto' : 'Adicionar novo Projeto'}</h2>
           <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-all"><X size={20} /></button>
        </div>

        <div className="bg-[#f8fafc] border-b border-gray-200 px-8 shrink-0">
           <div className="flex gap-4">
             <TabBtn label="Projeto" active={activeTab === 'project'} onClick={() => setActiveTab('project')} />
             {isManager && (
               <TabBtn label="Configurações do Projeto" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
             )}
           </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden bg-white">
          <div className="flex-1 overflow-y-auto p-10 space-y-8 scrollbar-hide">
            {activeTab === 'project' ? (
              <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
                 {/* Nome do Projeto */}
                 <div className="space-y-1.5">
                    <label className="text-xs font-black text-red-500 uppercase tracking-tight">* Nome do Projeto</label>
                    <input 
                      required 
                      className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-medium focus:ring-2 focus:ring-primary/20 text-slate-700 shadow-sm" 
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                    />
                 </div>

                 {/* Cliente */}
                 <div className="space-y-1.5">
                    <label className="text-xs font-black text-red-500 uppercase tracking-tight">* Cliente</label>
                    <div className="relative">
                      <select 
                        required
                        className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 appearance-none cursor-pointer pr-10 shadow-sm"
                        value={formData.clientId}
                        onChange={e => setFormData({...formData, clientId: e.target.value})}
                      >
                         <option value="">Selecionar e começar a digitar</option>
                         {clients.map((c: Client) => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                      </select>
                      <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                 </div>

                 {/* Progresso */}
                 <div className="space-y-3 p-4 bg-slate-50/50 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-2">
                       <input 
                         type="checkbox" 
                         className="w-4 h-4 rounded border-gray-300 accent-[#0f172a]" 
                         checked={formData.calculateProgressByTasks} 
                         onChange={e => setFormData({...formData, calculateProgressByTasks: e.target.checked})}
                       />
                       <span className="text-xs font-bold text-slate-600">Calcular progresso através de tarefas</span>
                    </div>
                    <div className="space-y-1">
                       <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Progresso {formData.progress}%</p>
                       <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div className="bg-green-500 h-full transition-all duration-500" style={{ width: `${formData.progress}%` }}></div>
                       </div>
                    </div>
                 </div>

                 {/* Tipo e Status */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-red-500 uppercase tracking-tight">* Tipo de faturamento</label>
                      <select className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 appearance-none cursor-pointer pr-10 shadow-sm" value={formData.billingType} onChange={e => setFormData({...formData, billingType: e.target.value})}>
                         <option>Nada selecionado</option>
                         <option>Preço Fixo</option>
                         <option>Horas do Projeto</option>
                         <option>Horas da Tarefa</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Status</label>
                      <select className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 appearance-none cursor-pointer pr-10 shadow-sm" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as ProjectStatus})}>
                         {Object.values(ProjectStatus).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                 </div>

                 {/* Horas e Membros */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Horas estimadas</label>
                      <input type="number" className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 shadow-sm" value={formData.estimatedHours} onChange={e => setFormData({...formData, estimatedHours: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Membros do Projeto</label>
                      <select className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 appearance-none cursor-pointer pr-10 shadow-sm" value={formData.members[0]} onChange={e => setFormData({...formData, members: [e.target.value]})}>
                         {users.map((u: User) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                 </div>

                 {/* Datas */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-red-500 uppercase tracking-tight">* Data de Início</label>
                      <input type="date" className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 shadow-sm" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Prazo</label>
                      <input type="date" className="w-full bg-white border border-gray-300 p-3 rounded-lg outline-none font-bold text-slate-600 shadow-sm" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} />
                    </div>
                 </div>

                 {/* Tags */}
                 <div className="space-y-1.5">
                    <label className="text-xs font-black text-slate-700 uppercase tracking-tight flex items-center gap-2"><Tag size={12} /> Tags</label>
                    <div className="flex border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 bg-white">
                       <input 
                        className="flex-1 p-3.5 outline-none font-bold text-slate-600 text-sm bg-white" 
                        placeholder="Adicione tags..."
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = (e.target as HTMLInputElement).value;
                            if (val) {
                              setFormData({...formData, tags: [...formData.tags, val]});
                              (e.target as HTMLInputElement).value = '';
                            }
                          }
                        }}
                       />
                    </div>
                    <div className="flex gap-2 flex-wrap mt-2">
                      {formData.tags.map(t => <Badge key={t as string} className="bg-primary/5 text-primary border-primary/20">{t as string}</Badge>)}
                    </div>
                 </div>

                 {/* Descrição - Rich Text Simplificado */}
                 <div className="space-y-2 pb-10">
                    <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Descrição do Projeto</label>
                    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 shadow-sm">
                      <div className="bg-slate-50 border-b border-gray-200 p-2 flex flex-wrap items-center gap-1">
                         <EditorBtn icon={Bold} command="bold" />
                         <EditorBtn icon={Italic} command="italic" />
                         <EditorBtn icon={Underline} command="underline" />
                         <div className="w-[1px] h-4 bg-gray-300 mx-1"></div>
                         <EditorBtn icon={List} command="insertUnorderedList" />
                         <EditorBtn icon={ListOrdered} command="insertOrderedList" />
                         <div className="w-[1px] h-4 bg-gray-300 mx-1"></div>
                         <EditorBtn icon={AlignLeft} command="justifyLeft" />
                         <EditorBtn icon={AlignCenter} command="justifyCenter" />
                         <EditorBtn icon={AlignRight} command="justifyRight" />
                         <div className="w-[1px] h-4 bg-gray-300 mx-1"></div>
                         <EditorBtn icon={ImageIcon} onClick={() => {
                           const url = prompt('Cole a URL da imagem:');
                           if (url) document.execCommand('insertImage', false, url);
                         }} />
                         <EditorBtn icon={Link2} onClick={() => {
                           const url = prompt('Cole o link:');
                           if (url) document.execCommand('createLink', false, url);
                         }} />
                      </div>
                      <div 
                        ref={editorRef}
                        contentEditable={true}
                        className="w-full p-6 min-h-[250px] outline-none font-medium text-slate-700 bg-white prose-slate max-w-none"
                        dangerouslySetInnerHTML={project ? { __html: project.description } : undefined}
                      />
                    </div>
                 </div>
              </div>
            ) : (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                 {/* Notificações e Envio */}
                 <div className="grid grid-cols-1 gap-6 pb-6 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="send-mail" className="w-4 h-4 rounded accent-[#0f172a]" checked={formData.sendEmailOnCreate} onChange={e => setFormData({...formData, sendEmailOnCreate: e.target.checked})} />
                      <label htmlFor="send-mail" className="text-sm font-bold text-slate-600 cursor-pointer">Enviar e-mail do projeto criado</label>
                    </div>
                    
                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-red-500 uppercase tracking-tight">* Enviar notificações de contatos</label>
                      <div className="relative group">
                         <select 
                           className="w-full bg-white border border-blue-500 p-3 rounded-lg outline-none font-bold text-slate-700 appearance-none cursor-pointer pr-10 shadow-[0_0_0_1px_rgba(59,130,246,0.5)] transition-all" 
                           value={formData.contactNotificationType} 
                           onChange={e => setFormData({...formData, contactNotificationType: e.target.value})}
                         >
                            <option value="Para todos os contatos com notificações de projetos ativados">Para todos os contatos com notificações de projetos ativados</option>
                            <option value="Contatos específicos">Contatos específicos</option>
                            <option value="Não envie notificações">Não envie notificações</option>
                         </select>
                         <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none" />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-tight">Abas visíveis</label>
                      <div className="relative">
                        <div className="w-full bg-white border border-gray-300 p-3 rounded-lg font-bold text-slate-600 flex flex-wrap gap-2 pr-10">
                          {formData.visibleTabs.map(t => <span key={t} className="bg-slate-100 px-2 py-0.5 rounded text-[10px]">{t}</span>)}
                        </div>
                        <ChevronDown size={18} className="absolute right-4 top-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                 </div>

                 {/* Permissões */}
                 <div className="space-y-1 border rounded-lg overflow-hidden border-gray-100 bg-slate-50/20 mb-10">
                    <PermissionToggle label="Permitir clientes a visualizar tarefas" active={formData.permissions.viewTasks} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTasks: !formData.permissions.viewTasks}})} />
                    <PermissionToggle label="Permitir clientes a criar tarefas" active={formData.permissions.createTasks} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, createTasks: !formData.permissions.createTasks}})} />
                    <PermissionToggle label="Permitir clientes a editar tarefas (somente tarefas criadas a partir do contato)" active={formData.permissions.editTasks} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, editTasks: !formData.permissions.editTasks}})} />
                    <PermissionToggle label="Permitir clientes a comentar nas tarefas do projeto" active={formData.permissions.commentTasks} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, commentTasks: !formData.permissions.commentTasks}})} />
                    <PermissionToggle label="Permitir clientes a visualizar comentários da tarefa" active={formData.permissions.viewTaskComments} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTaskComments: !formData.permissions.viewTaskComments}})} />
                    <PermissionToggle label="Permitir clientes a visualizar anexos das tarefas" active={formData.permissions.viewTaskAttachments} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTaskAttachments: !formData.permissions.viewTaskAttachments}})} />
                    <PermissionToggle label="Permitir clientes a visualizar os itens da lista de tarefas" active={formData.permissions.viewTaskChecklist} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTaskChecklist: !formData.permissions.viewTaskChecklist}})} />
                    <PermissionToggle label="Permitir clientes a enviar anexos nas tarefas" active={formData.permissions.uploadAttachments} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, uploadAttachments: !formData.permissions.uploadAttachments}})} />
                    <PermissionToggle label="Permitir clientes a visualizar o tempo total logado da tarefa" active={formData.permissions.viewTaskTotalTime} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTaskTotalTime: !formData.permissions.viewTaskTotalTime}})} />
                    <PermissionToggle label="Permitir clientes a ver visão geral das finanças" active={formData.permissions.viewFinanceOverview} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewFinanceOverview: !formData.permissions.viewFinanceOverview}})} />
                    <PermissionToggle label="Permitir clientes a atualizar os arquivos" active={formData.permissions.updateFiles} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, updateFiles: !formData.permissions.updateFiles}})} />
                    <PermissionToggle label="Permitir clientes a abrir discussões" active={formData.permissions.openDiscussions} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, openDiscussions: !formData.permissions.openDiscussions}})} />
                    <PermissionToggle label="Permitir clientes a visualizar marcos" active={formData.permissions.viewMilestones} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewMilestones: !formData.permissions.viewMilestones}})} />
                    <PermissionToggle label="Permitir clientes a visualizar Gantt" active={formData.permissions.viewGantt} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewGantt: !formData.permissions.viewGantt}})} />
                    <PermissionToggle label="Permitir clientes a visualizar quadros de horários" active={formData.permissions.viewTimesheets} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTimesheets: !formData.permissions.viewTimesheets}})} />
                    <PermissionToggle label="Permitir clientes a visualizar registros de atividades" active={formData.permissions.viewActivityLog} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewActivityLog: !formData.permissions.viewActivityLog}})} />
                    <PermissionToggle label="Permitir clientes a visualizar membros da equipe" active={formData.permissions.viewTeamMembers} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, viewTeamMembers: !formData.permissions.viewTeamMembers}})} />
                    <PermissionToggle label="Ocultar tarefas do projeto na tabela de tarefas principais (área administrativa)" active={formData.permissions.hideTasksFromAdmin} onToggle={() => setFormData({...formData, permissions: {...formData.permissions, hideTasksFromAdmin: !formData.permissions.hideTasksFromAdmin}})} />
                 </div>
              </div>
            )}
          </div>
          
          <div className="p-6 border-t border-gray-100 flex justify-end bg-white shrink-0 shadow-[0_-10px_20px_-15px_rgba(0,0,0,0.1)]">
             <button 
              type="submit" 
              disabled={isSaving}
              className="bg-[#1e293b] text-white px-10 py-3 rounded-xl font-bold text-sm hover:bg-black transition-all shadow-lg active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
             >
                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} strokeWidth={3} />} 
                {isSaving ? 'Salvando...' : 'Salvar Projeto'}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PermissionToggle = ({ label, active, onToggle }: { label: string, active: boolean, onToggle: () => void }) => (
  <div className="flex items-center gap-3 p-3 bg-white border-b border-gray-50 last:border-none hover:bg-gray-50/50 transition-colors">
     <input type="checkbox" className="w-4 h-4 rounded border-gray-300 accent-[#0f172a] cursor-pointer" checked={active} onChange={onToggle} />
     <span className="text-xs font-bold text-slate-600">{label}</span>
  </div>
);

const TabBtn = ({ label, active, onClick }: { label: string, active: boolean, onClick: () => void }) => (
  <button 
    type="button"
    onClick={onClick}
    className={`py-4 px-2 text-xs font-bold transition-all border-b-2 ${active ? 'border-[#0f172a] text-[#0f172a]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
  >
    {label}
  </button>
);

const EditorBtn = ({ icon: Icon, command, onClick }: any) => (
  <button 
    type="button" 
    onClick={() => {
      if (onClick) onClick();
      else if (command) document.execCommand(command, false);
    }}
    className="p-1.5 text-slate-400 hover:bg-gray-100 rounded transition-all"
  >
    <Icon size={14} />
  </button>
);

const StatusBadge = ({ label, value, color, onClick, active }: any) => (
  <button 
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-2 ${active ? 'bg-slate-100 border-slate-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
  >
    <span className="text-xs font-black text-slate-800">{value}</span>
    <span className={`text-[10px] font-bold uppercase ${color}`}>{label}</span>
  </button>
);

const getProjectStatusStyle = (s: ProjectStatus) => {
  switch(s) {
    case ProjectStatus.NAO_INICIADO: return 'bg-slate-100 text-slate-600 border-slate-200';
    case ProjectStatus.EM_PROGRESSO: return 'bg-blue-50 text-blue-600 border-blue-100';
    case ProjectStatus.AGUARDANDO: return 'bg-orange-50 text-orange-600 border-orange-100';
    case ProjectStatus.CANCELADO: return 'bg-red-50 text-red-600 border-red-100';
    case ProjectStatus.CONCLUIDO: return 'bg-green-50 text-green-600 border-green-100';
    default: return 'bg-gray-50 text-gray-500';
  }
};
